import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CONSOLE } from "@/constants/testIds";
import { Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { config, updateConfig } = useApp();
  const [form, setForm] = useState({
    proxy_address: "",
    implementation_address: "",
    network_name: "",
    chain_id: "",
    rpc_url: "",
    explorer_url: "",
    token_name: "",
    token_symbol: "",
  });
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (config) {
      setForm({
        proxy_address: config.proxy_address || "",
        implementation_address: config.implementation_address || "",
        network_name: config.network_name || "",
        chain_id: config.chain_id ?? "",
        rpc_url: config.rpc_url || "",
        explorer_url: config.explorer_url || "",
        token_name: config.token_name || "",
        token_symbol: config.token_symbol || "",
      });
    }
  }, [config]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSave = async () => {
    setBusy(true);
    try {
      await updateConfig({
        ...form,
        chain_id: form.chain_id ? Number(form.chain_id) : null,
      });
    } catch (e) {
      toast.error("Save failed", { description: e.message });
    } finally { setBusy(false); }
  };

  const F = ({ label, k, placeholder, testId, mono = false }) => (
    <div className="space-y-1">
      <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</Label>
      <Input data-testid={testId} value={form[k]} onChange={onChange(k)} placeholder={placeholder} className={mono ? "font-mono" : ""} />
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Settings</div>
        <h1 className="text-3xl font-semibold tracking-tight">PIO network & contract</h1>
        <p className="text-sm text-slate-500 mt-1">
          Point the console at your deployed PUSD proxy. When proxy + a connected wallet are set, the dashboard switches from DEMO to LIVE mode.
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-md max-w-3xl">
        <div className="border-b border-slate-200 px-5 py-3"><h3 className="text-sm font-semibold tracking-tight">Contract</h3></div>
        <div className="px-5 py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Proxy address" k="proxy_address" placeholder="0x…" mono testId={CONSOLE.proxyInput} />
          <F label="Implementation address" k="implementation_address" placeholder="0x…" mono />
          <F label="Token name" k="token_name" placeholder="PIO USD" />
          <F label="Token symbol" k="token_symbol" placeholder="PUSD" />
        </div>
        <div className="border-t border-b border-slate-200 px-5 py-3"><h3 className="text-sm font-semibold tracking-tight">Network</h3></div>
        <div className="px-5 py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Network name" k="network_name" placeholder="PIO Mainnet" testId={CONSOLE.networkInput} />
          <F label="Chain ID" k="chain_id" placeholder="e.g. 314159" mono testId={CONSOLE.chainIdInput} />
          <F label="RPC URL" k="rpc_url" placeholder="https://rpc.pio…" mono testId={CONSOLE.rpcInput} />
          <F label="Explorer URL" k="explorer_url" placeholder="https://explorer.pio…" mono testId={CONSOLE.explorerInput} />
        </div>
        <div className="border-t border-slate-200 px-5 py-4 flex justify-end">
          <Button
            data-testid={CONSOLE.saveConfigBtn}
            disabled={busy}
            onClick={onSave}
            className="rounded-md bg-slate-900 hover:bg-slate-800 text-white"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.75} />}
            Save configuration
          </Button>
        </div>
      </div>
    </div>
  );
}
